/**
 * =============================================================================
 * QUESTION-ACTIONS INTERACTION ISLAND (ADR-028 Slice 2B / BL-WIDGET-UXR-003B)
 * =============================================================================
 *
 * The canonical question-detail island. The server-rendered article remains the
 * SOLE representation of the question, answers, gallery, related content and
 * products (ADR-028 D-1/D-1a). This island owns ONLY interactive controls,
 * their live status, and permitted progressive enhancement of static nodes.
 *
 * It therefore NEVER renders `QuestionDetailView`, `QuestionDetail`, a second
 * question, a second answer list, or client-owned product/content cards, and it
 * never emits `data-fmp-client-question-content`.
 *
 * Ordering guarantee (the reason this is not a plain `createRoot(slot)`):
 * the slot's initial child is the ADR-029 S-1 static affordance. `createRoot`
 * takes ownership of its container and clears it on first commit, so mounting
 * directly on the slot would destroy that fallback BEFORE we know whether live
 * controls can work. Instead the island resolves availability and essential
 * control readiness first, stages React on a DETACHED container, waits for a
 * successful first commit, and only then performs ONE `replaceChildren` — so
 * there is no frame in which the slot is empty, and none in which the static
 * fallback and live controls both exist (ADR-029 S-11(b): "never stacking").
 *
 * =============================================================================
 */

// SURFACE_COST_QUESTION_ACTIONS_START
import React, { useCallback, useEffect, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ImageLightbox, type LightboxImage } from './components/ImageLightbox/ImageLightbox';
import { ReplyForm } from './components/ReplyForm';
import { useStyleInjection } from './hooks';
import { widgetApi } from './api/client';
import { logger } from './lib/logger';
import type { ResolvedMountPoint } from './mountContract';

// =============================================================================
// CONTRACT MARKERS (client-owned; no renderer ever emits these)
// =============================================================================

/** Marks the ONE live controls root. Asserted by the ADR-028 `-003B` gate. */
export const CLIENT_CONTROLS_ATTRIBUTE = 'data-fmp-client-controls';

/**
 * Deliberately never written. Named so the gate's "matches ZERO nodes"
 * assertion has a single, greppable definition on the client side.
 */
export const CLIENT_QUESTION_CONTENT_ATTRIBUTE = 'data-fmp-client-question-content';

/** Generic positional noun for lightbox labels — never filename-derived. */
const GALLERY_LABEL_NOUN = 'Question image';

// =============================================================================
// TYPES
// =============================================================================

export type QuestionActionsIslandV1Outcome =
  /** Live controls committed and atomically replaced the slot's children. */
  | 'live'
  /** Resolved non-VISIBLE, or availability could not be resolved. */
  | 'suppressed'
  /** VISIBLE, but an essential control capability was not ready. */
  | 'unready'
  /** Torn down (duplicate bootstrap / HMR / reset) before settling. */
  | 'cancelled'
  /** Defensive: attested topology was no longer usable at commit time. */
  | 'detached';

export interface QuestionActionsIslandV1Handle {
  /**
   * Resolves with the FIRST outcome the island reaches. Later transitions — a
   * post-commit revert, or teardown after going live — do not change it, because
   * a Promise settles once. Test seam.
   */
  readonly settled: Promise<QuestionActionsIslandV1Outcome>;
  /** Idempotent teardown with full reverse-direction restoration. */
  destroy(): void;
}

// =============================================================================
// GALLERY ENHANCEMENT (event delegation only — no node is moved or replaced)
// =============================================================================

/** Upper bound for a plausible intrinsic dimension, in CSS pixels. */
const MAX_INTRINSIC_DIMENSION = 20_000;
/** Longest image URL accepted, mirroring the Worker's `safeExternalImageUrl`. */
const MAX_IMAGE_URL_LENGTH = 2_000;
/**
 * Upper bound on how many gallery images are scanned. Honest pages carry at
 * most `MAX_IMAGES_PER_QUESTION`; the cap keeps a hostile or broken theme that
 * injects a huge subtree from blocking the merchant's main thread. 24 is a wide
 * margin over the governed `MAX_IMAGES_PER_QUESTION` (5), so it can only ever
 * bite pages the renderer did not produce.
 */
const MAX_GALLERY_IMAGES = 24;

/**
 * Upper bound on how many re-entrant island registrations are drained before a
 * new island claims the singleton. Each drained island's teardown can itself
 * re-enter, so the drain is bounded rather than a `while`: a merchant page that
 * re-bootstraps unconditionally from a teardown hook must degrade to a logged
 * warning, never to a hung storefront.
 */
const MAX_REENTRANT_DRAIN = 8;

function readIntrinsicSize(image: HTMLImageElement): Pick<LightboxImage, 'width' | 'height'> {
  // `naturalWidth/Height` are 0 until decode; fall back to the renderer's
  // attributes. ADR-022 §8 requires BOTH or NEITHER, never one alone.
  const width = image.naturalWidth || Number(image.getAttribute('width'));
  const height = image.naturalHeight || Number(image.getAttribute('height'));
  const usable = (value: number): boolean =>
    Number.isFinite(value) && value > 0 && value <= MAX_INTRINSIC_DIMENSION;
  return usable(width) && usable(height) ? { width, height } : {};
}

function readSafeImageUrl(image: HTMLImageElement): string | null {
  // `currentSrc` is the URL the browser ALREADY loaded, so opening the lightbox
  // never triggers a second fetch. Fall back to the literal attribute when the
  // image has not finished selecting a source yet.
  const raw = image.currentSrc || image.getAttribute('src') || '';
  if (raw.length === 0 || raw.length > MAX_IMAGE_URL_LENGTH) return null;
  try {
    const url = new URL(raw, image.ownerDocument.baseURI);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    // Parity with the Worker's `safeExternalImageUrl`: embedded credentials are
    // never a legitimate image source and are a known phishing/exfil shape.
    if (url.username.length > 0 || url.password.length > 0) return null;
    return url.href;
  } catch {
    return null;
  }
}

interface CollectedGalleryImage {
  readonly element: HTMLImageElement;
  readonly image: LightboxImage;
}

/**
 * Collects the static gallery's images without mutating or refetching them.
 *
 * Returns element/image PAIRS so the click handler can resolve an index against
 * the very same filtered pass the lightbox is given. Deriving the index from an
 * unfiltered `querySelectorAll` instead would diverge whenever a candidate is
 * rejected — exactly what a theme lazy-loader that swaps some `src`s for
 * placeholders produces — and would silently open the wrong photo.
 */
function collectGalleryImages(gallery: Element): CollectedGalleryImage[] {
  const collected: CollectedGalleryImage[] = [];
  const candidates = gallery.querySelectorAll('img');
  const limit = Math.min(candidates.length, MAX_GALLERY_IMAGES);
  for (let index = 0; index < limit; index += 1) {
    const candidate = candidates[index];
    const url = readSafeImageUrl(candidate);
    if (!url) continue;
    collected.push({ element: candidate, image: { url, ...readIntrinsicSize(candidate) } });
  }
  return collected;
}

// =============================================================================
// ISLAND COMPONENT
// =============================================================================

interface QuestionActionsIslandProps {
  readonly questionId: string;
  readonly turnstileSiteKey: string;
  readonly gallery: Element | null;
}

function QuestionActionsIsland({
  questionId,
  turnstileSiteKey,
  gallery,
}: QuestionActionsIslandProps): React.ReactElement {
  useStyleInjection(islandStyles, 'fmp-question-actions-island-styles');

  const [lightbox, setLightbox] = useState<{ images: LightboxImage[]; index: number } | null>(null);
  // Resolved during the FIRST render, not in an effect: the affordance must be
  // present in the very commit that replaces the slot's fallback, or the island
  // would introduce a second layout step (and FMP-attributed shift) right after
  // going live.
  //
  // Counted from the ELEMENTS the visitor can see, not from the collectable
  // URLs. A third-party lazy-loader that still holds placeholder `src`s at
  // commit time would otherwise yield zero and suppress the keyboard affordance
  // permanently — the one control the accessibility redesign exists to provide —
  // even after the real URLs arrive. `openLightbox` re-collects at open time and
  // bails harmlessly if nothing is showable.
  const [galleryCount] = useState(() =>
    gallery ? Math.min(gallery.querySelectorAll('img').length, MAX_GALLERY_IMAGES) : 0
  );

  const closeLightbox = useCallback(() => setLightbox(null), []);

  // Collected at OPEN time, never cached. Merchant themes routinely run lazy
  // loaders and image swappers, so a set captured at mount could hand the
  // lightbox URLs that no longer match what the page is showing.
  const openLightbox = useCallback(
    (target: HTMLImageElement | null) => {
      if (!gallery) return;
      const collected = collectGalleryImages(gallery);
      if (collected.length === 0) return;
      // Index resolved against the SAME filtered pass handed to the lightbox.
      // A target outside the collected set (e.g. past MAX_GALLERY_IMAGES) has no
      // honest index. Opening at 0 would show a DIFFERENT photo than the one
      // clicked — the exact failure the paired collection exists to prevent.
      const index = target ? collected.findIndex((entry) => entry.element === target) : 0;
      if (index < 0) return;
      setLightbox({ images: collected.map((entry) => entry.image), index });
    },
    [gallery]
  );

  // Progressive enhancement of the STATIC gallery.
  //
  // Deliberately NO attribute is written to any server-rendered node. An
  // earlier revision added role="button"/tabindex to the static <img>s; an
  // accessibility review established that those tab stops sit OUTSIDE
  // `.fmp-widget-scope`, where the widget's `:focus-visible` guarantee cannot
  // reach and a merchant `outline: none` reset would leave them with no visible
  // focus indicator (WCAG 2.4.7) — with no in-repo way to style or even detect
  // it, since `sourceGuards` forbids unscoped rules. `role="button"` also
  // destroyed the images' `img` role in the accessibility tree.
  //
  // Instead the keyboard affordance is an ISLAND-OWNED control, rendered inside
  // the token scope where focus treatment, forced-colors support and the 44px
  // product-standard target all apply. Clicking a photo stays as a pointer
  // shortcut through delegation, which needs no attributes at all. The static
  // gallery is therefore byte-identical before, during and after the island.
  useEffect(() => {
    if (!gallery) return;

    const onClick = (event: Event): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const image = target.closest('img');
      // Only claim the click once we know this image is one we can actually
      // show. Calling preventDefault() before that check would turn an
      // unopenable photo into a dead click that also suppresses the theme's own
      // default action.
      // Claim the click only for an image we can actually show AT THE RIGHT
      // INDEX; otherwise leave the theme's own default action intact.
      if (!image || !collectGalleryImages(gallery).some((entry) => entry.element === image)) {
        return;
      }
      event.preventDefault();
      openLightbox(image);
    };

    gallery.addEventListener('click', onClick);
    return () => gallery.removeEventListener('click', onClick);
  }, [gallery, openLightbox]);

  return (
    <div className="fmp-qa-island">
      {galleryCount > 0 && (
        <button
          type="button"
          className="fmp-qa-island__gallery-button"
          onClick={() => openLightbox(null)}
        >
          {galleryCount === 1 ? 'View photo' : `View ${galleryCount} photos`}
        </button>
      )}
      <ReplyForm questionId={questionId} turnstileSiteKey={turnstileSiteKey} defaultCollapsed />
      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          initialIndex={lightbox.index}
          onClose={closeLightbox}
          labelNoun={GALLERY_LABEL_NOUN}
        />
      )}
    </div>
  );
}

/**
 * Reports the first successful commit. Rendered as the last child inside the
 * error boundary, so a RENDER-phase crash replaces the children before any
 * effect runs and the commit signal never fires — the static fallback is never
 * replaced. An EFFECT-phase crash is different: React flushes this effect
 * anyway, so the island commits and `handleSubtreeError` then reverts to the
 * static fallback. `settled` reports `live` in that case; see its JSDoc.
 */
function CommitSentinel({ onCommit }: { onCommit: () => void }): null {
  useEffect(() => {
    onCommit();
  }, [onCommit]);
  return null;
}

// =============================================================================
// STYLES
// =============================================================================

const islandStyles = `
  :where(.fmp-widget-scope) .fmp-qa-island {
    display: flow-root;
    margin-block: var(--fmp-space-lg);
  }

  /* Island-owned keyboard affordance for the STATIC gallery. It lives inside
     the token scope precisely so the widget's focus, forced-colors and
     product-standard target guarantees all apply — the static images
     themselves are never given a tab stop the widget cannot style. */
  :where(.fmp-widget-scope) .fmp-qa-island__gallery-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-block-size: var(--fmp-size-44);
    min-inline-size: var(--fmp-size-44);
    margin-block-end: var(--fmp-space-md);
    padding: var(--fmp-space-sm) var(--fmp-space-lg);
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-control);
    background-color: var(--fmp-color-surface);
    color: var(--fmp-color-text);
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    font-family: inherit;
    cursor: pointer;
  }

  :where(.fmp-widget-scope) .fmp-qa-island__gallery-button:hover {
    background-color: var(--fmp-color-surface-hover);
  }

  :where(.fmp-widget-scope) .fmp-qa-island__gallery-button:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-primary);
    outline-offset: var(--fmp-outline-offset);
  }

  /* The collapsed reply CTA is the slot's primary target; pin the product
     standard rather than inheriting it from padding plus line-height. */
  :where(.fmp-widget-scope) .fmp-qa-island .fmp-reply-form__expand-btn {
    min-block-size: var(--fmp-size-44);
  }

  :where(.fmp-widget-scope) .fmp-qa-island__error {
    margin-block: var(--fmp-space-lg);
    color: var(--fmp-color-error-dark);
    font-size: var(--fmp-font-size-base);
  }
`;

// =============================================================================
// BOOTSTRAP INSTALL
// =============================================================================

/** Only ever one island per document. Deactivate-first on re-entry. */
let activeIsland: QuestionActionsIslandV1Handle | null = null;

/**
 * Tear down the installed island without letting a failure escape.
 *
 * `destroy()`'s own `finally` protects the island's internal state, but
 * `root.unmount()` re-throws whatever an effect cleanup throws — and one of
 * those cleanups calls `window.turnstile.remove()`, a third-party global the
 * merchant page owns. `bootstrap()` has no try/catch above it, so an escaping
 * throw would abort the rest of the widget's boot on a storefront we do not
 * control.
 */
function destroyActiveIslandSafely(): void {
  // Capture the island we are tearing down. `destroy()` can synchronously run
  // React effect cleanups, and a merchant-owned global invoked from one of them
  // can re-enter `install()` — which registers a NEW island in `activeIsland`.
  // Clearing unconditionally in `finally` would then deregister that healthy new
  // island without ever tearing it down: it would leak its root and listeners,
  // and the NEXT install would capture its live container as the slot's
  // "original" children and resurrect a dead React container on teardown instead
  // of restoring the server's static CTA.
  const tornDown = activeIsland;
  try {
    tornDown?.destroy();
  } catch (error) {
    logger.warn('QUESTION_ACTIONS_ISLAND_TEARDOWN_FAILED', {
      errorMessage: error instanceof Error ? error.message : 'unknown',
    });
  } finally {
    // Clear ONLY if the singleton still points at the island we tore down.
    if (activeIsland === tornDown) activeIsland = null;
  }
}

interface StaleAffordance {
  readonly element: HTMLElement;
  readonly hidden: boolean;
  /** Whole `style` attribute, or null when the element had none. */
  readonly style: string | null;
}

/**
 * ADR-028 D-1a v16 / ADR-029 S-11(b) v13 — the ONE narrow exception to the
 * never-touch rule. A page published under VISIBLE keeps serving its static CTA
 * after a mode downgrade until reconciliation republishes it. Where JS runs,
 * the island is the only actor holding the live resolved mode, so it SHOULD
 * HIDE (never remove) the stale affordance.
 *
 * SHOULD, not MUST: this is a silent no-op when the slot is empty (which is
 * every page today), and island-boot failure must degrade to the static page
 * rather than become a conformance failure. Only the slot is reachable through
 * a governed hook; the page-end static copy has no hook and is never touched.
 */
function hideStaleAffordance(slot: HTMLElement): StaleAffordance[] {
  const hidden: StaleAffordance[] = [];
  for (const child of Array.from(slot.children)) {
    if (!(child instanceof HTMLElement)) continue;
    hidden.push({
      element: child,
      hidden: child.hidden,
      style: child.getAttribute('style'),
    });
    child.hidden = true;
    // `hidden` alone loses to any theme rule that sets `display`; the important
    // declaration makes the honest-affordance outcome deterministic.
    child.style.setProperty('display', 'none', 'important');
  }
  return hidden;
}

function restoreStaleAffordance(entries: readonly StaleAffordance[]): void {
  for (const entry of entries) {
    entry.element.hidden = entry.hidden;
    // Exact restoration: an element that had no `style` attribute must not be
    // left with a residual empty one, which `removeProperty` alone would do.
    if (entry.style === null) entry.element.removeAttribute('style');
    else entry.element.setAttribute('style', entry.style);
  }
}

export interface InstallQuestionActionsIslandV1Options {
  /** Injected for tests; defaults to the shared widget API client. */
  readonly api?: Pick<typeof widgetApi, 'availability' | 'config' | 'isInitialized'>;
}

/**
 * Install the canonical `question-actions` island for an attested mount.
 *
 * Consumes the immutable `ResolvedMountPoint` produced by
 * `resolveSurfaceMountV1` — attested carrier, question id, static article,
 * gallery, accepted-answer hook and direct-child action slot. Identity is never
 * re-derived from the URL and the DOM is never re-queried for a competing
 * mount, so a host-page mutation after boot cannot make routing, identity and
 * the React root select different elements.
 */
export function installQuestionActionsIslandV1(
  mount: ResolvedMountPoint,
  options: InstallQuestionActionsIslandV1Options = {}
): QuestionActionsIslandV1Handle {
  const api = options.api ?? widgetApi;
  const hooks = mount.hooks;
  const questionId = mount.questionId;

  // Definite-assignment: the executor runs synchronously, so `settle` is bound
  // before any caller can reach it.
  let settle!: (outcome: QuestionActionsIslandV1Outcome) => void;
  const settled = new Promise<QuestionActionsIslandV1Outcome>((resolve) => {
    settle = resolve;
  });

  // Defensive: `resolveSurfaceMountV1` already fails closed on malformed
  // topology, so reaching this branch means the caller passed a non-attested
  // mount. Mutate nothing.
  if (!hooks || !questionId) {
    logger.warn('QUESTION_ACTIONS_ISLAND_NOT_ATTESTED', { mountId: mount.mountId });
    settle('detached');
    return { settled, destroy: () => undefined };
  }

  // Deactivate-first, but only AFTER attestation passes: a defensive call with a
  // non-attested mount must not tear down a healthy live island.
  destroyActiveIslandSafely();

  // `hooks.acceptedAnswer` is attested by the resolver but deliberately NOT
  // consumed: making the staff answer visually primary is ordering/emphasis on
  // server content, which ADR-029 S-11(a) owns and the observe-only permission
  // on `data-fmp-static-question` forbids this island from touching.
  const { questionActionsSlot: slot, staticQuestion: article, gallery } = hooks;
  const controller = new AbortController();

  let destroyed = false;
  let committed = false;
  let root: Root | null = null;
  let container: HTMLElement | null = null;
  let staleAffordance: readonly StaleAffordance[] = [];

  // Captured SYNCHRONOUSLY, before the first await, while the slot is provably
  // in its pristine server-rendered state. Capturing at commit time instead
  // would read it after an async window in which the host page could have
  // mutated it, making the teardown restore depend on timing rather than on the
  // server's actual output.
  const originalSlotChildren: ChildNode[] = Array.from(slot.childNodes);

  // Reaching a terminal outcome does NOT deregister the handle. A suppressed
  // island may still hold restorable state (a hidden stale affordance), so
  // dropping the singleton on settle would strand it with no path back —
  // `resetQuestionActionsIslandV1()` would find nothing to undo. Only
  // `destroy()` deregisters. Promise-resolve idempotence is what makes the
  // first outcome the reported one.
  const finish = settle;

  const discardStagedWork = ({ deferUnmount = false } = {}): void => {
    const discarded = root;
    root = null;
    container?.remove();
    container = null;
    if (!discarded) return;
    if (deferUnmount) {
      // `commit()` and the error boundary both run inside React's own commit
      // phase, where a synchronous `unmount()` is refused and merely scheduled
      // (with a console warning). Deferring one microtask keeps the teardown
      // synchronous-looking to callers without fighting the scheduler.
      queueMicrotask(() => discarded.unmount());
      return;
    }
    discarded.unmount();
  };

  const destroy = (): void => {
    if (destroyed) return;
    destroyed = true;
    controller.abort();
    try {
      // Restore BEFORE unmounting. `root.unmount()` re-throws anything an
      // effect cleanup throws; doing the restore first means such a throw can
      // never leave the slot empty — the worst case is a detached container
      // that was already removed from the slot by `replaceChildren`.
      // Only undo a replacement we actually performed. An island that never
      // committed has not touched the slot, so restoring would be a mutation of
      // its own.
      if (committed) slot.replaceChildren(...originalSlotChildren);
      restoreStaleAffordance(staleAffordance);
      staleAffordance = [];
    } finally {
      // The unmount must be ATTEMPTED even if the restore above threw. A hostile
      // host that poisons `slot.replaceChildren` would otherwise skip it entirely
      // and leave this island's React root mounted and unreachable — a leak that
      // survives teardown. Nested so a throw here still cannot skip
      // deregistration and settlement below.
      try {
        discardStagedWork();
      } finally {
        // Deregistration and settlement must survive any throw above, or a
        // failed teardown would wedge every later bootstrap (install() begins
        // with activeIsland?.destroy()).
        if (activeIsland === handle) activeIsland = null;
        settle('cancelled');
      }
    }
  };

  /**
   * A render-phase throw replaces `CommitSentinel` with the boundary fallback,
   * so the commit signal never arrives. Before commit that fallback is inside a
   * DETACHED container and is therefore unreachable — discard it and settle,
   * leaving the static page in place. After commit the container is live, so the
   * boundary's message is visible and the island stays as it is.
   */
  const handleSubtreeError = (): void => {
    if (destroyed) return;
    if (committed) {
      // Post-commit the container is live, so the boundary's message is on the
      // page — but an error panel is strictly worse for the visitor than the
      // server's own working affordance. Hand the static fallback back rather
      // than leaving them with neither controls nor a CTA. `settled` has already
      // reported `live` and reports only the FIRST terminal outcome.
      slot.replaceChildren(...originalSlotChildren);
      committed = false;
      discardStagedWork({ deferUnmount: true });
      logger.warn('QUESTION_ACTIONS_ISLAND_REVERTED_AFTER_CRASH', { questionId });
      return;
    }
    discardStagedWork({ deferUnmount: true });
    finish('unready');
  };

  const handle: QuestionActionsIslandV1Handle = { settled, destroy };
  // Deactivate-first ran BEFORE this point, but `destroy()` executes React effect
  // cleanups synchronously and one of them calls a merchant-owned global
  // (`TurnstileWidget` -> `window.turnstile.remove()`). A merchant global may
  // re-run the bootstrap, so a NEW island can have registered itself while our own
  // deactivate-first was still unwinding. Assigning over it here would deregister a
  // live island without ever destroying it: its React root, delegated listeners and
  // reference-counted <style> would leak for the life of the page.
  //
  // The identity guard in `destroyActiveIslandSafely` does not cover this — it
  // correctly declines to clear a singleton it no longer owns, and the clobber
  // happens here, in the caller. Drain any such re-entrant registrant before
  // claiming the slot. Bounded, because each drained island can itself re-enter.
  for (let drain = 0; activeIsland !== null && drain < MAX_REENTRANT_DRAIN; drain += 1) {
    destroyActiveIslandSafely();
  }
  if (activeIsland !== null) {
    logger.warn('QUESTION_ACTIONS_ISLAND_REENTRANT_DRAIN_EXHAUSTED', { questionId });
  }
  activeIsland = handle;

  const commit = (): void => {
    // FIRST commit only. React StrictMode intentionally double-invokes effects,
    // and a re-entrant commit would capture the live container as the "saved
    // fallback" and resurrect it on teardown.
    if (destroyed || committed || !container) return;
    // The attested topology must still be usable. A host page that replaced the
    // article underneath us gets its static page back, not an orphan root.
    // Re-assert the full attested relationship, not just connectedness: a page
    // that relocated the slot out of its carrier between resolve and commit no
    // longer satisfies the topology `resolveSurfaceMountV1` verified.
    // `article.isConnected && article.contains(slot)` already implies the slot is
    // connected, so only the three independent conditions are checked.
    if (!article.isConnected || !article.contains(slot) || slot.parentElement !== mount.carrier) {
      discardStagedWork({ deferUnmount: true });
      logger.warn('QUESTION_ACTIONS_ISLAND_DETACHED_BEFORE_COMMIT');
      finish('detached');
      return;
    }
    // ONE mutation: the static fallback leaves and the live root arrives
    // together. The root CONTAINER moves; its React-managed descendants never
    // leave it, so React's host bookkeeping stays valid.
    //
    // `committed` is armed only here — AFTER the topology check — so a
    // `detached` outcome can never make teardown "restore" a slot this island
    // never touched. `finally` guarantees the promise settles even if a hostile
    // host makes `replaceChildren` throw.
    try {
      slot.replaceChildren(container);
      committed = true;
      logger.info('QUESTION_ACTIONS_ISLAND_LIVE', { questionId });
    } finally {
      finish(committed ? 'live' : 'detached');
    }
  };

  const start = async (): Promise<void> => {
    if (!api.isInitialized()) {
      logger.info('QUESTION_ACTIONS_ISLAND_UNREADY', { questionId, reason: 'api-uninitialized' });
      finish('unready');
      return;
    }

    let visible = false;
    // D-1a separates two branches that look identical in the DOM but are NOT the
    // same governed outcome: a RESOLVED non-VISIBLE mode (v16 — may hide a stale
    // affordance) and a RESOLUTION FAILURE (v14 — must leave bytes identical).
    // The shipped client turns a transport failure into a `success: false`
    // envelope rather than a throw, so without this flag both would log
    // `not-visible` and the one production signal that distinguishes them would
    // be wrong for the reachable case.
    let modeResolved = false;
    try {
      const availability = await api.availability.get({ signal: controller.signal });
      if (destroyed) return;
      modeResolved = availability.success;
      visible = availability.success && availability.data.launchMode === 'VISIBLE';
      if (!visible && availability.success) {
        // A RESOLVED non-VISIBLE mode is the only trigger for the stale-CTA
        // exception. An unresolved mode (below) leaves static bytes untouched.
        staleAffordance = hideStaleAffordance(slot);
      }
    } catch {
      if (destroyed) return;
      // Fail closed exactly like the shipped availability gate. ADR-028 D-1a
      // v14: a resolution failure leaves static content byte-identical.
      logger.info('QUESTION_ACTIONS_ISLAND_SUPPRESSED', { questionId, reason: 'unresolved' });
      finish('suppressed');
      return;
    }

    if (!visible) {
      // The stale-affordance hide is the ONE place this island mutates
      // merchant-authored DOM, so it is never silent.
      logger.info('QUESTION_ACTIONS_ISLAND_SUPPRESSED', {
        questionId,
        reason: modeResolved ? 'not-visible' : 'unresolved-envelope',
        staleAffordanceHidden: staleAffordance.length,
      });
      finish('suppressed');
      return;
    }

    // Essential control readiness. Committing a ReplyForm with no Turnstile key
    // would replace a usable static affordance with a form that cannot submit.
    let turnstileSiteKey: unknown = '';
    try {
      const config = await api.config.get({ signal: controller.signal });
      if (destroyed) return;
      // Deliberately untyped: a malformed envelope from a proxy or a poisoned
      // fetch can carry any shape, and `(42).length === 0` is false — which
      // would commit unusable controls over a working static affordance.
      turnstileSiteKey = config.success ? (config.data.turnstileSiteKey as unknown) : '';
    } catch {
      if (destroyed) return;
    }
    if (typeof turnstileSiteKey !== 'string' || turnstileSiteKey.trim().length === 0) {
      logger.warn('QUESTION_ACTIONS_ISLAND_CAPABILITY_UNREADY', { questionId });
      finish('unready');
      return;
    }
    // Commit the value we VALIDATED. The gate above tests `.trim()`, so passing
    // the raw string on would hand Turnstile a key with surrounding whitespace
    // that the gate never approved — it would pass readiness and then fail at the
    // provider, which is the exact "usable affordance replaced by an unusable
    // one" outcome the gate exists to prevent.
    const readyTurnstileSiteKey = turnstileSiteKey.trim();

    // Stage on a DETACHED container. Nothing is visible and the slot is
    // untouched until the first commit succeeds.
    container = article.ownerDocument.createElement('div');
    container.setAttribute(CLIENT_CONTROLS_ATTRIBUTE, '1');
    root = createRoot(container);
    root.render(
      <React.StrictMode>
        <IslandErrorBoundary onError={handleSubtreeError}>
          <QuestionActionsIsland
            questionId={questionId}
            turnstileSiteKey={readyTurnstileSiteKey}
            gallery={gallery}
          />
          <CommitSentinel onCommit={commit} />
        </IslandErrorBoundary>
      </React.StrictMode>
    );
  };

  // The NON-await work — the readiness branches, `createRoot` and the first
  // `render` — sits outside both try blocks. An unhandled rejection on a
  // merchant storefront would also leave `settled` pending forever.
  void start().catch((error: unknown) => {
    logger.error(
      'QUESTION_ACTIONS_ISLAND_BOOTSTRAP_FAILED',
      error instanceof Error ? error : null,
      { questionId }
    );
    if (destroyed) return;
    discardStagedWork();
    finish('unready');
  });
  return handle;
}

/** Test/HMR isolation: tear down any installed island. */
export function resetQuestionActionsIslandV1(): void {
  destroyActiveIslandSafely();
}

// =============================================================================
// ERROR CONTAINMENT
// =============================================================================

interface IslandErrorBoundaryProps {
  readonly children: React.ReactNode;
  /** Terminal signal. A render-phase throw removes `CommitSentinel` from the
   *  tree, so without this the island could never settle and would leak a
   *  detached root for the life of the page. */
  readonly onError: () => void;
}

interface IslandErrorBoundaryState {
  hasError: boolean;
}

/**
 * Confines a control crash to the slot. The boundary lives INSIDE the root
 * container, so its fallback can never escape into host-page markup, and a
 * crash during the first render prevents the commit signal — leaving the static
 * fallback in place rather than replacing it with a broken subtree.
 */
class IslandErrorBoundary extends React.Component<
  IslandErrorBoundaryProps,
  IslandErrorBoundaryState
> {
  state: IslandErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): IslandErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    logger.error('QUESTION_ACTIONS_ISLAND_SUBTREE_ERROR', error);
    this.props.onError();
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="fmp-qa-island">
          <p className="fmp-qa-island__error" role="alert">
            These controls could not be loaded. Please reload the page to try again.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
// SURFACE_COST_QUESTION_ACTIONS_END
